import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { eventApiMessage } from '@/components/events/event-errors'
import { EventOrderForm } from '@/components/events/event-order-form'
import { DetailPage } from '@/components/templates/detail-page'
import { getJSON, postJSON } from '@/lib/api'
import { EventsListPage } from '@/pages/EventsListPage'
import { EventCheckInPage } from '@/pages/EventCheckInPage'
import { EventDetail, useEvent } from '@/pages/EventDetailPage'
import type { EventRecord, EventsView } from '@/pages/event-types'
import { EventChapterPicker } from '@/components/events/event-chapter-picker'
import { eventSegmentPath } from '@/pages/segmentEventPrefill'

export type { EventRecord, EventsView } from '@/pages/event-types'

export function EventsPage({
  view,
  navigateTo,
  canCreateSegments = false,
}: {
  view: EventsView
  navigateTo: (
    view: EventsView | { kind: 'event-registrations' } | { kind: 'communication-segment-new' },
    path: string,
  ) => void
  canCreateSegments?: boolean
}) {
  const duplicateEvent = useMutation({
    mutationFn: (event: EventRecord) =>
      postJSON<{ event: EventRecord }>(`/api/v1/events/${event.id}/duplicate`, {}),
    onSuccess: ({ event }) =>
      navigateTo({ kind: 'event-detail', eventId: event.id }, `/events/manage/${event.id}`),
  })
  if (view.kind === 'event-new')
    return (
      <NewEventPage
        onCancel={() => navigateTo({ kind: 'events-list' }, '/events')}
        onCreated={(event) =>
          navigateTo({ kind: 'event-detail', eventId: event.id }, `/events/manage/${event.id}`)
        }
      />
    )
  if (view.kind === 'event-detail')
    return (
      <EventDetail
        eventId={view.eventId}
        onSegment={
          canCreateSegments
            ? () =>
                navigateTo({ kind: 'communication-segment-new' }, eventSegmentPath(view.eventId))
            : undefined
        }
        onRegistrationsReport={() =>
          navigateTo(
            { kind: 'event-registrations' },
            `/reports/event-registrations?event_id=${view.eventId}`,
          )
        }
        onBack={() => navigateTo({ kind: 'events-list' }, '/events')}
        onDeleted={() => navigateTo({ kind: 'events-list' }, '/events')}
        onNewOrder={() =>
          navigateTo(
            { kind: 'event-order-new', eventId: view.eventId },
            `/events/manage/${view.eventId}/orders/new`,
          )
        }
        onCheckIn={() =>
          navigateTo(
            { kind: 'event-check-in', eventId: view.eventId },
            `/events/manage/${view.eventId}/check-in`,
          )
        }
      />
    )
  if (view.kind === 'event-check-in')
    return (
      <EventCheckInPage
        eventId={view.eventId}
        onDoorlist={() =>
          navigateTo(
            { kind: 'event-detail', eventId: view.eventId },
            `/events/manage/${view.eventId}`,
          )
        }
        onAddWalkIn={() =>
          navigateTo(
            { kind: 'event-order-new', eventId: view.eventId },
            `/events/manage/${view.eventId}/orders/new`,
          )
        }
      />
    )
  if (view.kind === 'event-order-new')
    return <NewEventOrderPage eventId={view.eventId} navigateTo={navigateTo} />
  return (
    <EventsListPage
      onNew={() => navigateTo({ kind: 'event-new' }, '/events/new')}
      onOpen={(event) =>
        navigateTo({ kind: 'event-detail', eventId: event.id }, `/events/manage/${event.id}`)
      }
      onDuplicate={(event) => duplicateEvent.mutate(event)}
      duplicateError={duplicateEvent.error ? eventApiMessage(duplicateEvent.error) : undefined}
      duplicating={duplicateEvent.isPending}
    />
  )
}

function NewEventOrderPage({
  eventId,
  navigateTo,
}: {
  eventId: number
  navigateTo: (view: EventsView, path: string) => void
}) {
  const queryClient = useQueryClient()
  const eventQuery = useEvent(eventId)
  const event = eventQuery.data?.event
  const back = () => navigateTo({ kind: 'event-detail', eventId }, `/events/manage/${eventId}`)
  return (
    <DetailPage
      title="New order"
      subtitle={event?.name ?? (eventQuery.isLoading ? 'Loading…' : 'Event unavailable')}
      backHref={`/events/manage/${eventId}`}
      backLabel="Orders"
      onBack={back}
    >
      {event && (
        <EventOrderForm
          event={event}
          onCancel={back}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['event-orders', eventId] })}
        />
      )}
    </DetailPage>
  )
}

function NewEventPage({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (event: EventRecord) => void
}) {
  const [name, setName] = React.useState('')
  const [startsAt, setStartsAt] = React.useState('')
  const [endsAt, setEndsAt] = React.useState('')
  const [chapterId, setChapterId] = React.useState<number | null>(null)
  const chaptersQuery = useQuery({
    queryKey: ['event-chapters'],
    queryFn: () => getJSON<{ chapters: { id: number; name: string }[] }>('/api/v1/chapters'),
    retry: false,
  })
  const createEvent = useMutation({
    mutationFn: () =>
      postJSON<{ event: EventRecord }>('/api/v1/events', {
        event: {
          name,
          starts_at: startsAt,
          ends_at: endsAt,
          chapter_id: chapterId,
          idempotency_key: crypto.randomUUID(),
        },
      }),
    onSuccess: ({ event }) => onCreated(event),
  })
  return (
    <DetailPage
      title="New event"
      subtitle="Start with the essentials. A free unlimited ticket is added automatically."
      backHref="/events"
      backLabel="Events"
      onBack={onCancel}
    >
      <form
        className="max-w-xl space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          createEvent.mutate()
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="event-name">Name</Label>
          <Input id="event-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        {(chaptersQuery.data?.chapters ?? []).length > 0 && (
          <EventChapterPicker
            chapters={chaptersQuery.data?.chapters ?? []}
            value={chapterId}
            onChange={setChapterId}
          />
        )}
        <div className="space-y-1.5">
          <Label htmlFor="event-start">Start date and time</Label>
          <Input
            id="event-start"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="event-end">End date and time (optional)</Label>
          <Input
            id="event-end"
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </div>
        {createEvent.error && <Alert>{eventApiMessage(createEvent.error)}</Alert>}
        <div className="flex gap-2">
          <Button type="submit" disabled={createEvent.isPending}>
            {createEvent.isPending ? 'Creating…' : 'Create event'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </DetailPage>
  )
}
